import { msg, t } from '@lingui/core/macro';
import { i18n, type MessageDescriptor } from 'shared/i18n/i18n';
import { ValidationResult } from './validate-url';

interface SocialNetworkConfig {
  /** Название сети для сообщения об ошибке; у VK оно своё в каждом языке */
  displayName: MessageDescriptor;
  /** Домены сети. Поддомены (www, m, music и т.п.) разрешены автоматически */
  domains: string[];
}

const SOCIAL_NETWORKS: Record<string, SocialNetworkConfig> = {
  vk: {
    displayName: msg`ВКонтакте`,
    // vk.ru — основной домен VK, vkontakte.ru — исторический
    domains: ['vk.com', 'vk.ru', 'vkontakte.ru'],
  },
  'vk-video': {
    displayName: msg`ВКвидео`,
    domains: ['vkvideo.ru', 'vkvideo.com', 'vk.video'],
  },
  telegram: {
    displayName: msg`Telegram`,
    domains: ['t.me', 'telegram.me', 'telegram.org'],
  },
  youtube: {
    displayName: msg`YouTube`,
    // youtube-nocookie.com отдаёт встраиваемый плеер
    domains: ['youtube.com', 'youtu.be', 'youtube-nocookie.com'],
  },
  instagram: {
    displayName: msg`Instagram`,
    domains: ['instagram.com', 'instagr.am'],
  },
};

/** Приводит hostname к сравнимому виду: нижний регистр и без завершающей точки FQDN */
function normalizeHostname(hostname: string): string {
  return hostname.toLowerCase().replace(/\.$/, '');
}

/** Совпадение с самим доменом или любым его поддоменом (`evil-vk.com` не проходит) */
function matchesDomain(hostname: string, domain: string): boolean {
  return hostname === domain || hostname.endsWith(`.${domain}`);
}

/**
 * Validates if a URL belongs to a specific social network.
 * Ensures that in fields for specific social networks, only URLs for that network can be added.
 *
 * Supported social networks (see SOCIAL_NETWORKS for the domain list):
 * - VK
 * - VK-Video
 * - Telegram
 * - YouTube
 * - Instagram
 *
 * @param url - The URL to validate
 * @param socialNetwork - The social network to check against
 * @returns ValidationResult - Object containing validation status and error message if invalid
 */
export function validateSocialNetworkUrl(url: string, socialNetwork: string): ValidationResult {
  if (!url) {
    return {
      isValid: false,
      errorMessage: t`URL не может быть пустым`,
    };
  }

  let hostname: string;
  try {
    // Create URL object to validate structure
    hostname = normalizeHostname(new URL(url).hostname);
  } catch (error) {
    // URL constructor will throw if the URL is invalid
    return {
      isValid: false,
      errorMessage: t`Неверный формат URL`,
    };
  }

  const config = SOCIAL_NETWORKS[socialNetwork.toLowerCase()];
  if (!config) {
    return {
      isValid: false,
      errorMessage: t`Неизвестная социальная сеть: ${socialNetwork}`,
    };
  }

  if (config.domains.some((domain) => matchesDomain(hostname, domain))) {
    return {
      isValid: true,
      errorMessage: null,
    };
  }

  const networkName = i18n._(config.displayName);
  return {
    isValid: false,
    errorMessage: t`Эта ссылка не принадлежит ${networkName}`,
  };
}
